"use client";

import { useState } from "react";
import { ChevronDown } from "lucide-react";
import type { ContactFaqItem } from "./ContactFaqSection";

interface ContactHelpTopicsSectionProps {
  title?: string;
  description?: string;
  items: ContactFaqItem[];
}

export default function ContactHelpTopicsSection({
  title = "What We Can Help You With",
  description,
  items,
}: ContactHelpTopicsSectionProps) {
  const visible = items
    .filter((item) => item.isVisible !== false && item.question.trim())
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const [openId, setOpenId] = useState<number | null>(null);

  if (!visible.length) return null;

  const toggle = (id: number) => {
    setOpenId((prev) => (prev === id ? null : id));
  };

  return (
    <section
      className="mt-24 mb-16 max-w-4xl md:mx-auto mx-5"
      aria-label={title}
    >
      <div className="mx-auto mb-10 max-w-xl text-center">
        {title.trim() ? (
          <h2 className="text-2xl sm:text-3xl font-extrabold text-[#767EF6]">
            {title}
          </h2>
        ) : null}
        {description?.trim() ? (
          <p className="mx-auto mt-6 max-w-md text-sm sm:text-base font-medium leading-7 text-[#F5F5F5]">
            {description}
          </p>
        ) : null}
      </div>

      <div>
        {visible.map((item, index) => {
          const isOpen = openId === index;
          const followsOpenItem = openId === index - 1;
          const topicId = `contact-help-topic-${index}`;
          const answerId = `contact-help-topic-answer-${index}`;
          const paragraphs = item.answer
            .split(/\n{2,}/)
            .map((paragraph) => paragraph.trim())
            .filter(Boolean);

          return (
            <div
              key={`${item.question}-${index}`}
              className={
                index === 0 ? "" : followsOpenItem ? "mt-8" : "-mt-px"
              }
            >
              <button
                id={topicId}
                type="button"
                onClick={() => toggle(index)}
                aria-expanded={isOpen}
                aria-controls={answerId}
                className="flex w-full items-center justify-between gap-4 border border-[#615D5D] px-6 py-7 text-left transition-colors hover:bg-[#232325] focus:outline-none focus:ring-2 focus:ring-[#767EF6] focus:ring-inset sm:px-10"
              >
                <span className="text-lg font-medium text-[#F5F5F5]">
                  {item.question}
                </span>
                {isOpen ? (
                  <ChevronDown
                    className="h-4 w-4 shrink-0 rotate-180 text-[#F5F5F5]"
                    aria-hidden
                  />
                ) : null}
              </button>
              <div
                id={answerId}
                role="region"
                aria-labelledby={topicId}
                aria-hidden={!isOpen}
                className={`grid overflow-hidden px-6 text-[#F5F5F5]/80 transition-[grid-template-rows,opacity] duration-300 ease-in-out sm:px-10 ${
                  isOpen
                    ? "grid-rows-[1fr] opacity-100"
                    : "grid-rows-[0fr] opacity-0"
                }`}
              >
                <div className="min-h-0 overflow-hidden">
                  <div className="max-w-3xl space-y-5 text-md leading-1">
                    {paragraphs.map((paragraph, paragraphIndex) => (
                      <p
                        key={paragraphIndex}
                        className={paragraphIndex === 0 ? "pt-10" : ""}
                      >
                        {paragraph}
                      </p>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
